import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  BarChart3,
  Blocks,
  Brain,
  Briefcase,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Compass,
  Factory,
  Focus,
  GitBranch,
  Home,
  Library,
  LifeBuoy,
  LogOut,
  Menu,
  MessageCircleMore,
  Rocket,
  Settings as SettingsIcon,
  ShieldCheck,
  ShoppingBag,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "../lib/authStore";
import { displayBusinessName } from "../lib/productNames";

const COMMAND_GROUPS = [
  {
    key: "operate",
    label: "Operate",
    note: "From idea through release",
    Icon: Compass,
    tone: "cyan",
    items: [
      ["/commandlinc", "Lifecycle Map", Home, { end: true }, "blue"],
      ["/commandlinc/venture-studio", "Venture Studio", Rocket, null, "purple"],
      ["/commandlinc/buildlinc", "BuildLinc", GitBranch, null, "teal"],
      ["/commandlinc/communications", "Communications", MessageCircleMore, null, "teal"],
      ["/commandlinc/missions", "Missions", Compass, null, "cyan"],
      ["/commandlinc/approvals", "Approval Inbox", CheckCircle2, { key: "approvals" }, "amber"],
      ["/commandlinc/qa", "QA Center", ShieldCheck, null, "green"],
    ],
  },
  {
    key: "intelligence",
    label: "Intelligence",
    note: "Agents, knowledge and analytics",
    Icon: Brain,
    tone: "purple",
    items: [
      ["/commandlinc/workforce", "AI Workforce", Users, null, "purple"],
      ["/commandlinc/brain", "Company Brain", Brain, null, "violet"],
      ["/commandlinc/analytics", "Analytics", BarChart3, null, "cyan"],
      ["/commandlinc/artifacts", "Artifact Library", Library, null, "blue"],
    ],
  },
  {
    key: "system",
    label: "System",
    note: "Cost, audit, integrations and support",
    Icon: Blocks,
    tone: "slate",
    items: [
      ["/commandlinc/cost", "Cost Center", CircleDollarSign, null, "green"],
      ["/commandlinc/ledger", "Activity Ledger", Activity, null, "slate"],
      ["/commandlinc/integrations", "Integrations", GitBranch, null, "purple"],
      ["/commandlinc/settings", "Settings", SettingsIcon, null, "blue"],
      ["/commandlinc/support", "Support", LifeBuoy, null, "green"],
    ],
  },
];

const BUSINESS_TONES = ["teal", "blue", "purple", "amber", "cyan", "green"];
const businessIcon = (key) => key === "linkforge" ? Factory : key === "gearlinc" ? ShoppingBag : Briefcase;
const businessRoute = (key) => key === "gearlinc" ? "/gearlinc" : key === "linkforge" ? "/linkforge" : `/businesses/${key}`;
const businessName = (business) => displayBusinessName(business);

function Item({ to, label, Icon, meta, pendingApprovals, location, onNavigate, tone = "blue" }) {
  return (
    <NavLink to={to} end={!!meta?.end} onClick={onNavigate} className={({ isActive }) => {
      const active = meta?.end ? location.pathname === to : isActive || location.pathname === to || location.pathname.startsWith(`${to}/`);
      return `lincnav-item tone-${tone}${active ? " active" : ""}`;
    }}>
      <span className="lincnav-item-icon"><Icon size={15} /></span>
      <span className="lincnav-item-copy">{label}</span>
      {meta?.key === "approvals" && pendingApprovals > 0 ? <em>{pendingApprovals}</em> : null}
      <ChevronRight className="lincnav-item-chevron" size={13} />
    </NavLink>
  );
}

function DrillCard({ label, note, Icon, tone = "blue", onClick, badge }) {
  return <button className={`lincnav-drill-card tone-${tone}`} onClick={onClick}><span className="lincnav-item-icon"><Icon size={15} /></span><span className="lincnav-drill-copy"><b>{label}</b>{note ? <small>{note}</small> : null}</span>{badge ? <em>{badge}</em> : null}<ChevronRight size={14} /></button>;
}

// Cross-app jump: used for destinations that live outside the LincWerks SPA
// (e.g. the ForgeLinc Merch Studio mounted at /linkforge/merch/*). Must be a
// plain <a href> so the browser does a full navigation through the proxy.
function ExternalItem({ href, label, Icon, tone = "blue", onNavigate }) {
  return (
    <a href={href} onClick={onNavigate} className={`lincnav-item tone-${tone}`}>
      <span className="lincnav-item-icon"><Icon size={15} /></span>
      <span className="lincnav-item-copy">{label}</span>
      <ChevronRight className="lincnav-item-chevron" size={13} />
    </a>
  );
}

export default function LincNav({ pendingApprovals = 0, businesses = [] }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [level, setLevel] = useState("root");
  const visibleBusinesses = useMemo(() => [...businesses].filter((business) => business.visible !== false && !business.self_managed).sort((a, b) => businessName(a).localeCompare(businessName(b))), [businesses]);
  const close = () => { setOpen(false); setLevel("root"); };

  useEffect(() => { setOpen(false); setLevel("root"); }, [location.pathname]);
  useEffect(() => { const onKeyDown = (event) => { if (event.key === "Escape") { setOpen(false); setLevel("root"); } }; window.addEventListener("keydown", onKeyDown); return () => window.removeEventListener("keydown", onKeyDown); }, []);

  const goHistoryBack = () => { close(); navigate(-1); };
  const onLogout = async () => { close(); await logout(); navigate("/login", { replace: true }); };
  const currentGroup = COMMAND_GROUPS.find((group) => level === `command:${group.key}`);
  const panelTitle = level === "root" ? "LinkNav" : level === "command" ? "CommandLinc" : level === "businesses" ? "Businesses" : currentGroup?.label || "LinkNav";
  const levelBack = () => { if (currentGroup) setLevel("command"); else setLevel("root"); };

  return (
    <>
      <div className="lincnav-float" aria-label="LincWerks navigation controls" data-edit-name="LinkNav controls">
        <button className="lincnav-float-button" onClick={goHistoryBack} title="Back" aria-label="Go back"><ArrowLeft size={17} /></button>
        <button className={`lincnav-launcher${open ? " is-open" : ""}`} onClick={() => open ? close() : setOpen(true)} aria-label={open ? "Close LinkNav" : "Open LinkNav"} aria-expanded={open} aria-controls="linkworks-navigation-panel" title="LinkNav">{open ? <X size={18} /> : <Menu size={18} />}<span>LINK</span></button>
        <button className="lincnav-float-button" onClick={onLogout} title="Sign out" aria-label="Sign out"><LogOut size={16} /></button>
      </div>
      <div className={`lincnav-backdrop${open ? " show" : ""}`} onClick={close} aria-hidden="true" />
      <section id="linkworks-navigation-panel" className={`lincnav-panel${open ? " show" : ""}`} aria-hidden={!open} data-edit-name="LinkNav">
        <div className="lincnav-mini-head">{level !== "root" ? <button className="lincnav-mini-icon" onClick={levelBack} aria-label="Back one LinkNav level" title="Back"><ChevronLeft size={16} /></button> : <span className="lincnav-mini-spacer" />}<b>{panelTitle}</b><button className="lincnav-mini-icon" onClick={close} aria-label="Close LinkNav" title="Close"><X size={16} /></button></div>
        <nav className="lincnav-card-stack">
          {level === "root" ? <><Item to="/focus" label="LincWerks" Icon={Focus} tone="cyan" meta={{ end: true }} location={location} onNavigate={close} /><Item to="/focus/queues" label="FocusLinc" Icon={Compass} tone="blue" location={location} onNavigate={close} /><DrillCard label="CommandLinc" note="Idea to operate" Icon={Blocks} tone="purple" onClick={() => setLevel("command")} badge={pendingApprovals || null} /><DrillCard label="Businesses" note="Ventures and tools" Icon={Briefcase} tone="teal" onClick={() => setLevel("businesses")} /><ExternalItem href="/linkforge/merch/collections" label="Merch Studio" Icon={ShoppingBag} tone="amber" onNavigate={close} /><Item to="/commandlinc/settings" label="Settings" Icon={SettingsIcon} tone="blue" location={location} onNavigate={close} /></> : null}
          {level === "command" ? COMMAND_GROUPS.map((group) => <DrillCard key={group.key} label={group.label} note={group.note} Icon={group.Icon} tone={group.tone} onClick={() => setLevel(`command:${group.key}`)} badge={group.key === "operate" && pendingApprovals ? pendingApprovals : null} />) : null}
          {currentGroup ? currentGroup.items.map(([to, label, Icon, meta, tone]) => <Item key={to} to={to} label={label} Icon={Icon} tone={tone} meta={meta} pendingApprovals={pendingApprovals} location={location} onNavigate={close} />) : null}
          {level === "businesses" ? <><Item to="/businesses" label="All businesses" Icon={Briefcase} tone="teal" meta={{ end: true }} location={location} onNavigate={close} />{visibleBusinesses.map((business, index) => { const key = business.key || business.slug; const Icon = businessIcon(key); const tone = BUSINESS_TONES[index % BUSINESS_TONES.length]; return <Item key={key} to={businessRoute(key)} label={businessName(business)} Icon={Icon} tone={tone} location={location} onNavigate={close} />; })}</> : null}
        </nav>
      </section>
    </>
  );
}
